import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const TradingContext = createContext(null);

export function TradingProvider({ children }) {
  const [leaderboard, setLeaderboard] = useState([]);
  const [openPositions, setOpenPositions] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch leaderboard data
  const fetchLeaderboard = useCallback(async () => {
    try {
      const res = await fetch('/api/leaderboard');
      if (!res.ok) throw new Error('Failed to fetch leaderboard');
      const response = await res.json();
      // Handle paginated response format
      const data = response.data || response;
      setLeaderboard(data);
    } catch (err) {
      console.error('Leaderboard error:', err);
      setError(err.message);
    }
  }, []);

  // Fetch open positions
  const fetchOpenPositions = useCallback(async () => {
    try {
      const res = await fetch('/api/trades/open');
      if (!res.ok) throw new Error('Failed to fetch positions');
      const data = await res.json();
      setOpenPositions(data);
    } catch (err) {
      console.error('Positions error:', err);
    }
  }, []);

  // Fetch system status
  const fetchSystemStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/system/status');
      if (!res.ok) throw new Error('Failed to fetch status');
      const data = await res.json();
      setSystemStatus(data);
    } catch (err) {
      console.error('Status error:', err);
    }
  }, []);

  // Fetch current price
  const fetchCurrentPrice = useCallback(async () => {
    try {
      const res = await fetch('/api/market/current');
      if (!res.ok) throw new Error('Failed to fetch price');
      const data = await res.json();
      setCurrentPrice(data);
    } catch (err) {
      console.error('Price error:', err);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchLeaderboard(),
        fetchOpenPositions(),
        fetchSystemStatus(),
        fetchCurrentPrice()
      ]);
      setLoading(false);
    };

    fetchAll();

    // Refresh data periodically
    const interval = setInterval(() => {
      fetchLeaderboard();
      fetchOpenPositions();
      fetchSystemStatus();
      fetchCurrentPrice();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [fetchLeaderboard, fetchOpenPositions, fetchSystemStatus, fetchCurrentPrice]);

  // SSE connection for real-time updates with auto-reconnect
  useEffect(() => {
    let eventSource = null;
    let reconnectTimeout = null;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 10;
    const baseReconnectDelay = 1000;

    const connect = () => {
      eventSource = new EventSource('/api/events/stream');

      eventSource.onopen = () => {
        console.log('SSE connected');
        reconnectAttempts = 0; // Reset on successful connection
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          switch (data.type) {
            case 'decision_cycle_complete':
              fetchLeaderboard();
              fetchOpenPositions();
              break;
            case 'position_closed':
              fetchLeaderboard();
              fetchOpenPositions();
              // Notify equity curve component to refresh
              window.dispatchEvent(new CustomEvent('trade_closed'));
              break;
            case 'price_update':
              setCurrentPrice(prev => ({ ...prev, price: data.price }));
              break;
            default:
              break;
          }
        } catch (err) {
          console.error('SSE parse error:', err);
        }
      };

      eventSource.onerror = (err) => {
        console.error('SSE connection error:', err);
        eventSource.close();

        // Auto-reconnect with exponential backoff
        if (reconnectAttempts < maxReconnectAttempts) {
          const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts);
          console.log(`SSE reconnecting in ${delay}ms (attempt ${reconnectAttempts + 1}/${maxReconnectAttempts})`);
          reconnectTimeout = setTimeout(() => {
            reconnectAttempts++;
            connect();
          }, delay);
        } else {
          console.error('SSE max reconnect attempts reached');
        }
      };
    };

    connect();

    return () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [fetchLeaderboard, fetchOpenPositions]);

  const value = {
    leaderboard,
    openPositions,
    systemStatus,
    currentPrice,
    loading,
    error,
    refreshLeaderboard: fetchLeaderboard,
    refreshPositions: fetchOpenPositions,
    refreshStatus: fetchSystemStatus
  };

  return (
    <TradingContext.Provider value={value}>
      {children}
    </TradingContext.Provider>
  );
}

export function useTrading() {
  const context = useContext(TradingContext);
  if (!context) {
    throw new Error('useTrading must be used within a TradingProvider');
  }
  return context;
}

export default TradingContext;
